import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../components/Header';

const API_URL = process.env.REACT_APP_API_URL || '';
const MIN_WORDS_OPEN_QUESTION = 10; // Mínimo de palabras para preguntas abiertas

const scaleLabels = ['Muy en desacuerdo', 'En desacuerdo', 'Neutral', 'De acuerdo', 'Muy de acuerdo'];

const countWords = (text) => {
  if (!text || !text.trim()) return 0;
  return text.trim().split(/\s+/).filter(word => word.length > 0).length;
};

function Survey() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [structure, setStructure] = useState(null);
  const [currentSection, setCurrentSection] = useState(0);
  const [scaleAnswers, setScaleAnswers] = useState({});
  const [openAnswers, setOpenAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [surveyInfo, setSurveyInfo] = useState(null);
  const [showValidationError, setShowValidationError] = useState(false);
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        // Validar token
        const validateRes = await fetch(`${API_URL}/api/survey/validate/${token}`);
        if (!validateRes.ok) {
          const data = await validateRes.json();
          throw new Error(data.error);
        }
        const info = await validateRes.json();
        setSurveyInfo(info);

        // Cargar estructura
        const structRes = await fetch(`${API_URL}/api/survey/structure`);
        const structData = await structRes.json();
        setStructure(structData);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [token]);

  if (loading) {
    return (
      <div className="survey-container">
        <Header subtitle="Cargando..." />
        <div className="empty-state">Cargando encuesta...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="survey-container">
        <Header subtitle="Error" />
        <main className="survey-main">
          <div className="public-error">
            <h2>No se puede acceder a la encuesta</h2>
            <p style={{ marginTop: '10px' }}>{error}</p>
          </div>
        </main>
      </div>
    );
  }

  const totalSections = structure.dimensiones.length + structure.preguntasAbiertas.length;
  const progress = ((currentSection + 1) / totalSections) * 100;

  const isScaleSection = currentSection < structure.dimensiones.length;
  const currentData = isScaleSection
    ? structure.dimensiones[currentSection]
    : structure.preguntasAbiertas[currentSection - structure.dimensiones.length];

  const handleScaleAnswer = (dimension, preguntaIndex, value) => {
    setScaleAnswers(prev => ({
      ...prev,
      [`${dimension}_${preguntaIndex}`]: value
    }));
    setShowValidationError(false);
  };

  const handleOpenAnswer = (seccion, preguntaIndex, value) => {
    setOpenAnswers(prev => ({
      ...prev,
      [`${seccion}_${preguntaIndex}`]: value
    }));
    setShowValidationError(false);
  };

  // Check if current section is complete
  const isSectionComplete = () => {
    if (isScaleSection) {
      // All scale questions must be answered
      return currentData.preguntas.every((_, idx) =>
        scaleAnswers[`${currentData.id}_${idx}`] !== undefined
      );
    } else {
      // All open questions must have minimum word count
      return currentData.preguntas.every((_, idx) => {
        const answer = openAnswers[`${currentData.id}_${idx}`] || '';
        return countWords(answer) >= MIN_WORDS_OPEN_QUESTION;
      });
    }
  };

  // Check if specific question is answered
  const isQuestionAnswered = (idx) => {
    if (isScaleSection) {
      return scaleAnswers[`${currentData.id}_${idx}`] !== undefined;
    } else {
      const answer = openAnswers[`${currentData.id}_${idx}`] || '';
      return countWords(answer) >= MIN_WORDS_OPEN_QUESTION;
    }
  };

  // Check if entire survey is complete
  const isSurveyComplete = () => {
    // Check all scale questions
    for (const dim of structure.dimensiones) {
      for (let i = 0; i < dim.preguntas.length; i++) {
        if (scaleAnswers[`${dim.id}_${i}`] === undefined) {
          return false;
        }
      }
    }
    // Check all open questions
    for (const sec of structure.preguntasAbiertas) {
      for (let i = 0; i < sec.preguntas.length; i++) {
        const answer = openAnswers[`${sec.id}_${i}`] || '';
        if (countWords(answer) < MIN_WORDS_OPEN_QUESTION) {
          return false;
        }
      }
    }
    return true;
  };

  const handleNext = () => {
    if (!isSectionComplete()) {
      setShowValidationError(true);
      setAttemptedSubmit(true);
      return;
    }
    if (currentSection < totalSections - 1) {
      setCurrentSection(prev => prev + 1);
      setShowValidationError(false);
      setAttemptedSubmit(false);
      window.scrollTo(0, 0);
    }
  };

  const handlePrev = () => {
    if (currentSection > 0) {
      setCurrentSection(prev => prev - 1);
      setShowValidationError(false);
      setAttemptedSubmit(false);
      window.scrollTo(0, 0);
    }
  };

  const handleSubmit = async () => {
    if (!isSurveyComplete()) {
      setShowValidationError(true);
      setAttemptedSubmit(true);
      return;
    }

    setSubmitting(true);
    try {
      const formattedScaleAnswers = Object.entries(scaleAnswers).map(([key, valor]) => {
        const lastUnderscore = key.lastIndexOf('_');
        const dimension = key.substring(0, lastUnderscore);
        const preguntaIndex = parseInt(key.substring(lastUnderscore + 1));
        return { dimension, preguntaIndex, valor };
      });

      const formattedOpenAnswers = Object.entries(openAnswers).map(([key, respuesta]) => {
        const lastUnderscore = key.lastIndexOf('_');
        const seccion = key.substring(0, lastUnderscore);
        const preguntaIndex = parseInt(key.substring(lastUnderscore + 1));
        return { seccion, preguntaIndex, respuesta };
      });

      const res = await fetch(`${API_URL}/api/survey/submit/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          scaleAnswers: formattedScaleAnswers,
          openAnswers: formattedOpenAnswers
        })
      });

      if (res.ok) {
        navigate('/gracias');
      } else {
        const data = await res.json();
        throw new Error(data.error);
      }
    } catch (err) {
      alert('Error al enviar la encuesta: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const getUnansweredCount = () => {
    if (isScaleSection) {
      return currentData.preguntas.filter((_, idx) =>
        scaleAnswers[`${currentData.id}_${idx}`] === undefined
      ).length;
    } else {
      return currentData.preguntas.filter((_, idx) => {
        const answer = openAnswers[`${currentData.id}_${idx}`] || '';
        return countWords(answer) < MIN_WORDS_OPEN_QUESTION;
      }).length;
    }
  };

  return (
    <div className="survey-container">
      <Header subtitle="Encuesta de Clima Laboral 2025" />
      <main className="survey-main">
        <div className="survey-progress">
          <div className="progress-bar">
            <div className="progress-fill progress-fill--primary" style={{ width: `${progress}%` }} />
          </div>
          <div className="survey-progress__text">
            <span>Sección {currentSection + 1} de {totalSections}</span>
            <span>{Math.round(progress)}% completado</span>
          </div>
        </div>

        {showValidationError && (
          <div className="survey-validation-error">
            <strong>⚠️ Por favor, completa todas las preguntas antes de continuar.</strong>
            {isScaleSection ? (
              <p style={{ margin: '8px 0 0 0' }}>
                Faltan {getUnansweredCount()} pregunta(s) por responder en esta sección.
              </p>
            ) : (
              <p style={{ margin: '8px 0 0 0' }}>
                Las respuestas abiertas deben tener al menos {MIN_WORDS_OPEN_QUESTION} palabras.
                Faltan {getUnansweredCount()} pregunta(s) por completar.
              </p>
            )}
          </div>
        )}

        <div className="survey-section">
          <h2 className="survey-section__title">{currentData.nombre}</h2>

          {isScaleSection ? (
            currentData.preguntas.map((pregunta, idx) => {
              const isAnswered = isQuestionAnswered(idx);
              const showError = attemptedSubmit && !isAnswered;
              return (
                <div key={idx} className="survey-question">
                  <p className="survey-question__text">
                    {idx + 1}. {pregunta}
                    <span className="survey-required-badge">Obligatoria</span>
                  </p>
                  <div className="survey-scale">
                    {[1, 2, 3, 4, 5].map((value) => (
                      <div key={value} className="survey-scale__option">
                        <button
                          className={
                            'survey-scale__btn'
                            + (scaleAnswers[`${currentData.id}_${idx}`] === value ? ' survey-scale__btn--selected' : '')
                            + (showError ? ' survey-scale__btn--error' : '')
                          }
                          onClick={() => handleScaleAnswer(currentData.id, idx, value)}
                        >
                          {value}
                        </button>
                        <div className="survey-scale__label">{scaleLabels[value - 1]}</div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })
          ) : (
            currentData.preguntas.map((pregunta, idx) => {
              const answer = openAnswers[`${currentData.id}_${idx}`] || '';
              const wordCount = countWords(answer);
              const isComplete = wordCount >= MIN_WORDS_OPEN_QUESTION;
              const showError = attemptedSubmit && !isComplete;
              return (
                <div key={idx} className="survey-question">
                  <p className="survey-question__text">
                    {idx + 1}. {pregunta}
                    <span className="survey-required-badge">Obligatoria</span>
                  </p>
                  <textarea
                    className={'survey-textarea' + (showError ? ' survey-textarea--error' : '')}
                    placeholder={`Escribe tu respuesta aquí (mínimo ${MIN_WORDS_OPEN_QUESTION} palabras)...`}
                    value={answer}
                    onChange={(e) => handleOpenAnswer(currentData.id, idx, e.target.value)}
                  />
                  <div className="survey-word-count">
                    <span className="hint" style={{ fontStyle: 'italic' }}>
                      Mínimo {MIN_WORDS_OPEN_QUESTION} palabras requeridas
                    </span>
                    <span className={isComplete ? 'survey-word-count--ok' : 'survey-word-count--error'}>
                      {wordCount} / {MIN_WORDS_OPEN_QUESTION} palabras
                      {isComplete && ' ✓'}
                    </span>
                  </div>
                </div>
              );
            })
          )}

          <div className="survey-nav">
            {currentSection > 0 && (
              <button
                className="survey-nav__btn survey-nav__btn--prev"
                onClick={handlePrev}
              >
                Anterior
              </button>
            )}
            {currentSection < totalSections - 1 ? (
              <button
                className="survey-nav__btn survey-nav__btn--next"
                onClick={handleNext}
                style={currentSection === 0 ? { flex: 1 } : undefined}
              >
                Siguiente
              </button>
            ) : (
              <button
                className="survey-nav__btn survey-nav__btn--submit"
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting ? 'Enviando...' : 'Enviar Encuesta'}
              </button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

export default Survey;
